import { apiFetchInit, apiUrl } from "@/lib/apiOrigin";

export function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  return fetch(apiUrl(path), { cache: "no-store", ...apiFetchInit(), ...init });
}

type ApiResult<T> = { ok: boolean; status: number; data: T | null; error?: string };

function errorFromBody(data: unknown, status: number, statusText: string, rawText?: string): string {
  if (data && typeof data === "object") {
    const row = data as { error?: unknown; message?: unknown };
    if (typeof row.error === "string" && row.error.trim()) return row.error.trim();
    if (typeof row.message === "string" && row.message.trim()) return row.message.trim();
  }
  const text = (rawText || "").trim();
  if (text && !text.startsWith("<") && text.length < 300) return text;
  const label = (statusText || "").trim();
  if (label) return label;
  return status > 0 ? `Request failed (HTTP ${status})` : "Request failed";
}

async function apiRequest<T>(path: string, init: RequestInit): Promise<ApiResult<T>> {
  const url = apiUrl(path);
  try {
    const res = await fetch(url, { cache: "no-store", ...apiFetchInit(), ...init });
    const rawText = await res.text().catch(() => "");
    let data = null as T | null;
    if (rawText) {
      try {
        data = JSON.parse(rawText) as T;
      } catch {
        data = null;
      }
    }
    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        data,
        error: errorFromBody(data, res.status, res.statusText, rawText),
      };
    }
    return { ok: true, status: res.status, data };
  } catch (e) {
    return {
      ok: false,
      status: 0,
      data: null,
      error: e instanceof Error ? e.message : "Network error",
    };
  }
}

export async function apiGet<T>(path: string): Promise<ApiResult<T>> {
  return apiRequest<T>(path, { method: "GET" });
}

export async function apiPost<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  return apiRequest<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function apiPatch<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  return apiRequest<T>(path, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function apiPut<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  return apiRequest<T>(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function apiDelete<T>(path: string): Promise<ApiResult<T>> {
  return apiRequest<T>(path, { method: "DELETE" });
}
